import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, Check, Loader2, Users, BookOpen, Building2, FileText, Award, ArrowRight, Download } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ICONS = { school_kit_starter: BookOpen, school_kit_classroom: Users, school_kit_department: Building2, school_kit_district: Award };

const INCLUDES = {
  school_kit_starter:    ["20 student accounts (1 year)", "2 pop-up book digital editions", "Teacher Pack PDF (lesson plans + rubrics)", "Priority email support"],
  school_kit_classroom:  ["40 student accounts (1 year)", "3 pop-up book digital editions", "Teacher Pack PDF (lesson plans + rubrics)", "Weekly office-hours access"],
  school_kit_department: ["120 student accounts (1 year)", "4 pop-up book digital editions", "Teacher Pack PDF + printable posters", "Dedicated Slack channel"],
  school_kit_district:   ["500 student accounts (1 year)", "All 4 pop-up book digital editions", "3 school licences · district reporting", "Onboarding call + admin dashboard"],
};

export default function SchoolKits() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [kits, setKits] = useState({});
  const [owned, setOwned] = useState([]);
  const [buying, setBuying] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/paypal/school-kits/catalog").then(({ data }) => setKits(data?.kits || {})).finally(() => setLoading(false));
    if (user) api.get("/paypal/school-kits/mine").then(({ data }) => setOwned(data?.kits || [])).catch(() => {});
  }, [user]);

  const buy = async (sku, label) => {
    if (!user) { toast.info("Sign in first — we'll bring you back."); nav("/signup?next=/school-kits"); return; }
    setBuying(sku);
    try {
      const { data } = await api.post("/paypal/orders/create", { sku });
      if (!data?.approve_url) throw new Error("No PayPal URL");
      sessionStorage.setItem("dpa_pending_checkout", JSON.stringify({ sku, label, recurring: false, at: Date.now() }));
      window.location.assign(data.approve_url);
    } catch (e) { setBuying(null); toast.error(e?.response?.data?.detail || "Could not start checkout"); }
  };

  const ownedSet = new Set((owned || []).map((k) => k.sku));

  return (
    <div className="container-narrow py-12 md:py-16" data-testid="school-kits-page">
      <div className="text-center mb-10">
        <div className="label-eyebrow mb-3 inline-flex items-center gap-2"><GraduationCap className="w-3.5 h-3.5" /> For schools and classrooms</div>
        <h1 className="font-heading text-4xl md:text-6xl font-semibold leading-tight">
          One kit. <span className="italic text-marigold">Every student.</span>
        </h1>
        <p className="text-ink2 mt-4 max-w-2xl mx-auto">
          A classroom pack that unlocks every app on Durga Programming Adventures for your students,
          plus digital pop-up books and a Teacher Pack with lesson plans and rubrics.
        </p>
      </div>

      {/* Teacher pack callout */}
      <div className="rounded-3xl p-6 md:p-8 mb-12 bg-saffron/10 border-2 border-saffron/40 grid md:grid-cols-3 gap-6 items-center" data-testid="school-kits-teacher-pack">
        <div className="md:col-span-2">
          <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Teacher Pack (included)</div>
          <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-2">Everything a teacher needs to run the road trip.</h2>
          <p className="text-ink2 text-sm leading-relaxed">
            Lesson plans mapped to each Journey checkpoint, printable rubrics, worksheet PDFs, and
            &ldquo;explain-this-to-a-9-year-old&rdquo; scripts written by Ms. Pradhan herself. Ready to download
            the moment your kit is active.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <PackTile icon={<FileText className="w-4 h-4" />} label="Lesson plans" />
          <PackTile icon={<Award className="w-4 h-4" />} label="Rubrics" />
          <PackTile icon={<BookOpen className="w-4 h-4" />} label="Worksheets" />
          <PackTile icon={<Users className="w-4 h-4" />} label="Parent notes" />
        </div>
      </div>

      {/* Kits */}
      {loading ? (
        <div className="text-center py-20 text-ink2"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5" data-testid="school-kits-grid">
          {Object.entries(kits).map(([sku, k]) => {
            const Icon = ICONS[sku] || GraduationCap;
            const isOwned = ownedSet.has(sku);
            const perSeat = (parseFloat(k.price) / k.seats).toFixed(2);
            return (
              <div key={sku} className="rounded-3xl border-2 border-ink bg-cream p-6 shadow-[8px_8px_0_0_#1A1B41] relative" data-testid={`school-kit-${sku}`}>
                {sku === "school_kit_classroom" && (
                  <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-marigold text-cream text-[10px] font-bold uppercase tracking-widest border-2 border-ink shadow-[3px_3px_0_0_#1A1B41]">
                    Most popular
                  </div>
                )}
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-11 h-11 rounded-2xl bg-teal/15 text-teal flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-semibold leading-tight">{k.name}</h3>
                    <div className="text-xs text-ink2 mt-0.5">Up to {k.seats} students · 1 year · ${perSeat}/seat</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-3xl font-semibold">${k.price}</div>
                    <div className="text-[10px] uppercase tracking-widest text-ink2 font-bold">per year</div>
                  </div>
                </div>
                <ul className="space-y-1.5 mb-5 text-sm">
                  {(INCLUDES[sku] || []).map((line, i) => (
                    <li key={i} className="flex items-start gap-2 text-ink2">
                      <Check className="w-4 h-4 text-teal flex-shrink-0 mt-0.5" /> {line}
                    </li>
                  ))}
                </ul>
                {isOwned ? (
                  <div className="rounded-xl bg-teal/10 border border-teal/40 px-3 py-2.5 text-sm text-teal inline-flex items-center gap-2 w-full justify-center" data-testid={`school-kit-owned-${sku}`}>
                    <Check className="w-4 h-4" /> Kit active · <a href="/school-kits/manage" className="underline">manage seats</a>
                  </div>
                ) : (
                  <button onClick={() => buy(sku, k.name)} disabled={buying === sku} className="btn-primary w-full" data-testid={`school-kit-buy-${sku}`}>
                    {buying === sku ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Get this kit <ArrowRight className="w-4 h-4" /></>}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Owned kits — teacher's admin view */}
      {owned.length > 0 && (
        <section className="mt-14" data-testid="school-kits-mine">
          <h2 className="font-heading text-2xl font-semibold mb-4">Your active kits</h2>
          <div className="space-y-3">
            {owned.map((k) => (
              <div key={k.id} className="p-4 rounded-2xl border border-border bg-cream flex items-center gap-4">
                <GraduationCap className="w-5 h-5 text-teal flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold text-sm truncate">{k.name}</div>
                  <div className="text-xs text-ink2">{k.seats_used || 0} of {k.seats} seats used · expires {(k.expires_at || "").slice(0, 10)}</div>
                </div>
                <a href="/dashboard" className="btn-outline-ink text-xs inline-flex items-center gap-1.5">
                  <Download className="w-3.5 h-3.5" /> Teacher Pack
                </a>
              </div>
            ))}
          </div>
        </section>
      )}

      <p className="text-xs text-ink2 mt-10 text-center max-w-xl mx-auto">
        Volume pricing scales from ~$12/seat to ~$9/seat as your cohort grows.
        Need something bigger? Email <a href="mailto:schools@durgaprogrammingadventures.com" className="text-marigold hover:underline">schools@durgaprogrammingadventures.com</a>.
      </p>
    </div>
  );
}

function PackTile({ icon, label }) {
  return (
    <div className="rounded-xl p-3 bg-cream border-2 border-ink flex flex-col items-center gap-1 shadow-[3px_3px_0_0_#1A1B41]">
      {icon}
      <div className="text-[10px] uppercase tracking-widest font-bold text-ink2">{label}</div>
    </div>
  );
}
